import { useCallback, useContext } from "react";
import { useStore } from "react-redux";

import { useGetVersionQuery } from "fond/api";
import { MapContext } from "fond/map/MapProvider";
import { useSaveLayerDrawing } from "fond/project/panels/useSaveLayerDrawing";
import { PolygonState, selectDrawExistingOption, selectDrawNewOption } from "fond/project/polygon/redux";
import { beginEditing, closeEditing, closePolygonSelect } from "fond/project/redux";
import { selectBaseDataDrawingKey, setBaseDataDrawingKey } from "fond/redux/baseDataDraw";
import { Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { BaseDataLayerKey } from "../layers";

import { BASE_DATA_LAYER_GROUP_IDS } from "./layerGroups";
import { useBeginBoundarySelect, useSaveBoundary } from "./useBoundary";

export type BaseDataDraw = {
  /** The base data layer being drawn on the map, if any. */
  drawingKey: BaseDataLayerKey | null;
  /** Start drawing a layer; the boundary starts from the existing one unless `fromScratch`. */
  startDraw(key: BaseDataLayerKey, options?: { fromScratch?: boolean }): void;
  cancel(): void;
  finish(): Promise<void>;
};

/**
 * Return the base data layer being drawn on the map, with actions to start, discard and save the drawing.
 *
 * Drawing the boundary draws the version's input data area and leaves polygon select open so the data inside it can be imported.
 */
export const useBaseDataDraw = (): BaseDataDraw => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const drawingKey = useAppSelector(selectBaseDataDrawingKey);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const { isDirty, setDrawMode } = useContext(MapContext);
  const saveLayerDrawing = useSaveLayerDrawing();
  const { saveBoundary } = useSaveBoundary();
  const beginBoundarySelect = useBeginBoundarySelect();

  const startDraw = useCallback(
    (key: BaseDataLayerKey, { fromScratch = false }: { fromScratch?: boolean } = {}) => {
      if (key === "boundary") {
        beginBoundarySelect();
        dispatch(version?.UploadedArea && !fromScratch ? selectDrawExistingOption(version.UploadedArea) : selectDrawNewOption());
      } else {
        dispatch(beginEditing(BASE_DATA_LAYER_GROUP_IDS[key]));
      }
      dispatch(setBaseDataDrawingKey(key));
    },
    [beginBoundarySelect, dispatch, version?.UploadedArea]
  );

  const cancel = useCallback(() => {
    if (drawingKey === "boundary") {
      dispatch(closePolygonSelect());
    } else if (drawingKey) {
      setDrawMode("no_feature");
      dispatch(closeEditing());
    }
    dispatch(setBaseDataDrawingKey(null));
  }, [dispatch, drawingKey, setDrawMode]);

  const finish = useCallback(async () => {
    if (drawingKey === "boundary") {
      const { polygonState, selectedArea } = store.getState().project.polygon;
      if (polygonState !== PolygonState.complete) return;
      await saveBoundary(selectedArea);
    } else if (drawingKey) {
      if (!isDirty) {
        cancel();
        return;
      }
      await saveLayerDrawing(BASE_DATA_LAYER_GROUP_IDS[drawingKey]);
    }
    dispatch(setBaseDataDrawingKey(null));
  }, [cancel, dispatch, drawingKey, isDirty, saveBoundary, saveLayerDrawing, store]);

  return { drawingKey, startDraw, cancel, finish };
};
